import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";

function InvoiceSeriesDialog({ isOpen, onClose, globalUser, onUpdate }) {
  const [seriesNumber, setSeriesNumber] = useState("1");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && globalUser?.uid) {
      fetchCurrentSeries();
    }
  }, [isOpen, globalUser]);

  const fetchCurrentSeries = async () => {
    try {
      const settingsRef = doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        setSeriesNumber(data.currentSeries || "1");
      } else {
        setSeriesNumber("1");
      }
    } catch (error) {
      console.error("Error fetching invoice series:", error);
      setSeriesNumber("1");
    }
  };

  const handleSave = async () => {
    if (!seriesNumber || seriesNumber.trim() === "") {
      toast.error("Please enter a valid series number");
      return;
    }

    setLoading(true);
    try {
      const settingsRef = doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries");
      await setDoc(settingsRef, {
        currentSeries: seriesNumber.trim(),
        updatedAt: new Date().toISOString(),
      });

      toast.success("Invoice series updated successfully");

      if (onUpdate) {
        onUpdate(seriesNumber.trim());
      }

      onClose();
    } catch (error) {
      console.error("Error updating invoice series:", error);
      toast.error("Failed to update invoice series");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSeriesNumber("1");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="invoice-series-modal bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Update Invoice Series</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Invoice Series Type */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Invoice Series Type
              </label>
              <div className="bg-[#0f0f23] border border-gray-700 px-4 py-3 rounded-lg">
                <p className="text-white">Default</p>
              </div>
            </div>

            {/* Series Number */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Series Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={seriesNumber}
                onChange={(e) => setSeriesNumber(e.target.value)}
                placeholder="Enter series (e.g., 1, 100, A1)"
                className="w-full px-4 py-3 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
              />
              <p className="text-xs text-gray-500 mt-2">
                Examples: "1" for numeric (1, 2, 3...) or "A1" for alphanumeric (A1, A2, A3...)
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoiceSeriesDialog;
